import React from 'react';
import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { API_URL } from '../../../Utils/url';

export const FONT = 'Mulish, sans-serif';

export const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

// Plain fetch for calls where the shared Webservice would pop up a toast on every response.
export const apiPost = async (route, body) => {
  const res = await fetch(`${API_URL}${route}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken() },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw Object.assign(new Error(data.message || data.error || 'Request failed'), { data });
  return data;
};

export const STATUS = {
  draft: { label: 'Draft', bg: '#f1f5f9', fg: '#475569' },
  pending: { label: 'Starting', bg: '#fef3c7', fg: '#92400e' },
  scheduled: { label: 'Scheduled', bg: '#dbeafe', fg: '#1d4ed8' },
  running: { label: 'Sending', bg: '#dcfce7', fg: '#166534' },
  paused: { label: 'Paused', bg: '#ffedd5', fg: '#9a3412' },
  completed: { label: 'Completed', bg: '#d1fae5', fg: '#065f46' },
  cancelled: { label: 'Stopped', bg: '#fee2e2', fg: '#991b1b' },
};
export const LOG_STATUS = {
  pending: { label: 'Waiting', bg: '#fef3c7', fg: '#92400e' },
  sending: { label: 'Sending', bg: '#dbeafe', fg: '#1d4ed8' },
  sent: { label: 'Sent', bg: '#dcfce7', fg: '#166534' },
  failed: { label: 'Failed', bg: '#fee2e2', fg: '#991b1b' },
  skipped: { label: 'Skipped', bg: '#f1f5f9', fg: '#475569' },
  cancelled: { label: 'Not sent', bg: '#f1f5f9', fg: '#475569' },
};

export const StatusChip = ({ status, map = STATUS }) => {
  const s = map[status] || { label: status || '-', bg: '#f1f5f9', fg: '#475569' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: s.bg, color: s.fg }} />;
};

export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
export const pct = (part, total) => (total ? Math.round((part / total) * 100) : 0);

// "sent / total" progress bar used in the list and on the tracking page.
export const Progress = ({ stats = {}, status, height = 8 }) => {
  const total = stats.total || 0;
  const done = (stats.sent || 0) + (stats.failed || 0) + (stats.skipped || 0);
  return (
    <Box sx={{ minWidth: 140 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{stats.sent || 0} / {total} sent</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{pct(done, total)}%</Typography>
      </Box>
      <LinearProgress
        variant={status === 'running' && !done ? 'indeterminate' : 'determinate'}
        value={pct(done, total)}
        sx={{ height, borderRadius: height, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: status === 'cancelled' ? '#ef4444' : status === 'paused' ? '#f97316' : 'linear-gradient(90deg,#10b981,#059669)' } }}
      />
    </Box>
  );
};

// Score meter for the spam check: 0 is best.
export const SpamMeter = ({ report, loading }) => {
  if (!report) return null;
  const color = report.level === 'good' ? '#10b981' : report.level === 'warning' ? '#f59e0b' : '#ef4444';
  const label = report.level === 'good' ? 'Looks good' : report.level === 'warning' ? 'Could be improved' : 'Likely to land in spam';
  return (
    <Box sx={{ opacity: loading ? 0.6 : 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color }}>{label}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>Spam risk {report.score}/100</Typography>
      </Box>
      <LinearProgress variant="determinate" value={report.score} sx={{ height: 10, borderRadius: 5, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: color } }} />
    </Box>
  );
};
